import React, { useState, useMemo } from 'react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isBefore,
  startOfDay,
  isAfter,
} from 'date-fns';
import clsx from 'clsx';
import { ReactComponent as ChevronIcon } from 'images/chevron.svg';

import styles from './AvailabilityCalendar.module.scss';

interface CalendarProps {
  selectedDate?: Date;
  availableDateKeys: string[];
  heading?: string;
  onDateSelect: (date: Date) => void;
  onMonthChange?: (month: Date) => void;
}

const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const AvailabilityCalendar: React.FC<CalendarProps> = ({
  selectedDate,
  availableDateKeys,
  heading,
  onDateSelect,
  onMonthChange,
}) => {
  const today = startOfDay(new Date());
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
  }, [currentMonth]);

  const availableDatesSet = useMemo(() => new Set(availableDateKeys), [availableDateKeys]);

  const handleMonthChange = (targetMonth: Date) => {
    setCurrentMonth(targetMonth);
    onMonthChange?.(targetMonth);
  };

  const handlePrevMonth = () => {
    handleMonthChange(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    handleMonthChange(addMonths(currentMonth, 1));
  };

  const isSelected = (date: Date) => selectedDate && isSameDay(date, selectedDate);
  const isAvailable = (date: Date) => availableDatesSet.has(format(date, 'yyyy-MM-dd'));

  const handleDateSelect = (date: Date) => {
    const isDeselecting = selectedDate && isSameDay(date, selectedDate);
    onDateSelect(date);

    if (!isDeselecting) {
      setCurrentMonth(startOfMonth(date));
    }
  };

  const showPrevMonthButton = !isSameMonth(currentMonth, today) && isAfter(currentMonth, today);

  return (
    <div className={styles.calendarContainer}>
      <div className={styles.header}>
        {showPrevMonthButton && (
          <button className={clsx(styles.navButton, styles.navButtonPrev)} onClick={handlePrevMonth} type="button">
            <ChevronIcon className={clsx(styles.chevron, styles.left)} />
          </button>
        )}
        <h2 className={styles.monthTitle}>{format(currentMonth, 'MMMM')}</h2>
        <button className={clsx(styles.navButton, styles.navButtonNext)} onClick={handleNextMonth} type="button">
          <ChevronIcon className={clsx(styles.chevron, styles.right)} />
        </button>
      </div>
      <p className={styles.subtitle}>{heading}</p>

      <div className={styles.grid}>
        {weekDays.map((day, index) => (
          <div key={`weekday-${index}`} className={styles.dayHeader}>
            {day}
          </div>
        ))}
        {calendarDays.map((day) => {
          const isPast = isBefore(day, today);
          const isDisabled = isPast || !isAvailable(day);
          return (
            <button
              key={day.toString()}
              className={clsx(styles.dayCell, {
                [styles.otherMonth]: !isSameMonth(day, currentMonth),
                [styles.pastDate]: isPast,
              })}
              onClick={() => handleDateSelect(day)}
              type="button"
              disabled={isDisabled}
            >
              <div
                className={clsx(styles.dayContent, {
                  [styles.selected]: isSelected(day),
                  [styles.isAvailable]: isAvailable(day),
                })}
              >
                {format(day, 'd')}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
